import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import cancelIcon from "../assets/images/cancel-btn.svg";
import { getMunicipalityApi, fetchLastScansApi, markScansAsSeenApi, searchPatientByAllFieldApi, getCitiesMunicipalitiesApi } from "../services/apiService";
import { useUserContext } from "../context/UserContext";
import { useOnClickOutside } from "../utils/commonData";
import { validateDzIdNumber, validateDzPhoneNumber } from "../utils/validationUtils";
import PatientSearchResult from "./PatientSearchResult";
import "./AddAppointmentForm.css";
import { getNationalUsersApi } from "../services/apiService";
// National users (users table)
import { useCitiesMunicipalities } from '@/queries/useLocalisationQuery'
import { useLastScans } from "@/queries/useAppointmentsQuery";


const AddAppointmentForm = ({
  isOpen,
  onClose,
  initialData = {},
  onSuccess
}) => {
  const { userData } = useUserContext();
  const scanHistoryRef = useRef();




  // Form state
  const [addAppObject, setAddAppObject] = useState({});
  const [addAppObjectInitial, setAddAppObjectInitial] = useState({});
  const [isUpdated, setIsUpdated] = useState(false);
  const [nationalUsers, setNationalUsers] = useState([]);
  const [hasNationalDuplicate, setHasNationalDuplicate] = useState(false);

  // Patient search
  const [searchPatientList, setSearchPatientList] = useState([]);
  const [patientSearchTimeoutId, setPatientSearchTimeoutId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // MRZ Scanner
  const [showScanHistory, setShowScanHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [historyPage, setHistoryPage] = useState(1);

  // Location data
  const [communesList, setCommunesList] = useState([]);
  const [wilayasList, setWilayasList] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);

  useOnClickOutside(scanHistoryRef, () => setShowScanHistory(false));

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const daysarray = Array.from({ length: 31 }, (_, i) => (i + 1 < 10 ? "0" + (i + 1) : String(i + 1)));
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 101 }, (_, i) => currentYear - i);

  // Initialize form with initial data
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setAddAppObject(initialData);
      setAddAppObjectInitial(initialData);
    }
  }, [initialData]);
  let doctorId = userData?.users?.type == "Doctor" ? userData?.users?.id : userData?.users?.parent_id;

  const {
    data: scanHistory,
    isLoading: loadingScansHistory,
    refetch: refetchScanHistory
  } = useLastScans(userData?.users?.id)



  function handleScansReload(){
    refetchScanHistory()
  }



  // Mark scans as seen when dropdown is opened
  const markCurrentScansAsSeen = async () => {
    if (!doctorId || !scanHistory) return;
    const unseenScans = scanHistory.filter(scan => !scan.is_seen);
    if (unseenScans.length === 0) return;

    const scanIds = unseenScans.map(scan => scan.id);
    try {
      await markScansAsSeenApi(doctorId, scanIds);
      // Update local state to mark scans as seen
      refetchScanHistory(doctorId)
    } catch (err) {
      console.error('Error marking scans as seen:', err);
    }
  };

  useEffect(() => {
    if (!doctorId) return;

    const fetchNationalUsers = async () => {
      try {
        const response = await getNationalUsersApi(doctorId);

        console.log("🔵 RAW API response:", response);
        console.log("🟢 National users (response.data.users):", response?.data?.users);

        if (response?.data?.success && Array.isArray(response.data.users)) {
          setNationalUsers(response.data.users);
        } else {
          setNationalUsers([]);
        }
      } catch (err) {
        console.error("🔴 Erreur récupération national users:", err);
        setNationalUsers([]);
      }
    };

    fetchNationalUsers();
  }, []);


  // Handle input changes
  const handleInputChange = (field, value) => {
    setAddAppObject(prev => {
      const updatedObject = { ...prev, [field]: value };

      // Check if values differ from initial
      const hasChanges = JSON.stringify(updatedObject) !== JSON.stringify(addAppObjectInitial);
      setIsUpdated(hasChanges);

      return updatedObject;
    });
  };

  // Patient search with debouncing - only search when user actually types in search fields
  useEffect(() => {
    if (Object.keys(addAppObject).length > 0) {
      // Check if any search field has actual content
      const hasSearchContent = Boolean(
        addAppObject?.patientFirstName?.trim() ||
        addAppObject?.patientLastName?.trim() ||
        addAppObject?.patientNumber?.trim() ||
        addAppObject?.idNumber?.trim()
      );

      // Only proceed if user has typed something in search fields
      if (!hasSearchContent) {
        setSearchPatientList([]);
        return;
      }

      if (patientSearchTimeoutId) {
        clearTimeout(patientSearchTimeoutId);
      }

      const newTimeoutId = setTimeout(() => {
        getPatientDetailsList(addAppObject);
      }, 500); // Increased to 500ms for tablets performance

      setPatientSearchTimeoutId(newTimeoutId);

      return () => clearTimeout(newTimeoutId);
    }
  }, [addAppObject]);

  // Normalise le genre pour comparaison
  const normalizeGender = (g) => {
    if (!g) return "";
    return g.toLowerCase() === "m" || g.toLowerCase() === "male"
      ? "male"
      : "female";
  };

  // Normalise le téléphone (supprime les espaces, +213 → 0 si besoin)
  const normalizePhone = (phone) => {
    if (!phone) return "";
    return phone.replace(/\s+/g, "").trim();
  };

  // Normalise la date au format YYYY-MM-DD
  const normalizeDOB = (year, month, day) => {
    if (!year || !month || !day) return "";
    const m = month.toString().padStart(2, "0");
    const d = day.toString().padStart(2, "0");
    return `${year}-${m}-${d}`;
  };

  const checkNationalDuplicate = () => {
    if (!nationalUsers.length) return false;

    const first = (addAppObject?.patientFirstName || "").trim().toLowerCase();
    const last = (addAppObject?.patientLastName || "").trim().toLowerCase();
    const gender = normalizeGender(addAppObject?.patientGender);
    const dob = normalizeDOB(addAppObject?.patientYear, addAppObject?.patientMonth, addAppObject?.patientDay);
    const birthCity = addAppObject?.birthCity || "";
    const municipality = addAppObject?.municipality_id || "";
    const phone = normalizePhone(addAppObject?.patientNumber);

    if (!first || !last || !gender) return false;

    return nationalUsers.some(user => {
      const userFirst = (user.first_name || "").trim().toLowerCase();
      const userLast = (user.last_name || "").trim().toLowerCase();
      const userGender = normalizeGender(user.gender);
      const userDOB = user.dob || ""; // Assurez-vous que la date est au format YYYY-MM-DD
      const userCity = user.birth_city?.toString() || "";
      const userMunicipality = user.municipality_id?.toString() || "";
      const userPhone = normalizePhone(user.cell_phone || user.phone || "");

      return (
        first === userFirst &&
        last === userLast &&
        gender === userGender &&
        dob === userDOB &&
        birthCity.toString() === userCity &&
        municipality.toString() === userMunicipality &&
        phone === userPhone
      );
    });
  };


  useEffect(() => {
    setHasNationalDuplicate(checkNationalDuplicate());
  }, [
    addAppObject?.patientFirstName,
    addAppObject?.patientLastName,
    addAppObject?.patientGender,
    nationalUsers
  ]);

  // Fetch patient list based on search criteria
  const getPatientDetailsList = async (searchObject) => {
    setIsSearching(true);
    try {
      const payload = {};

      // Build search term from name, phone, or ID number
      const searchTerms = [];
      if (searchObject?.patientFirstName) searchTerms.push(searchObject.patientFirstName);
      if (searchObject?.patientLastName) searchTerms.push(searchObject.patientLastName);
      if (searchObject?.patientNumber) searchTerms.push(searchObject.patientNumber);
      if (searchObject?.idNumber) searchTerms.push(searchObject.idNumber);
      if (searchTerms.length > 0) {
        payload.search = searchTerms.join(' ');
      }

      // Convert gender format: 'male'/'female' -> 'M'/'F'
      if (searchObject?.patientGender) {
        payload.gender = searchObject.patientGender === 'male' ? 'M' : 'F';
      }

      // Wilaya (state/province)
      if (searchObject?.birthCity) {
        payload.wilayaId = searchObject.birthCity;
      }

      // Commune / Municipalité
      if (searchObject?.municipality_id) {
        payload.municipality_id = Number(searchObject.municipality_id);
      }


      // Combine date parts into YYYY-MM-DD format for age/DOB filter
      if (searchObject?.patientYear && searchObject?.patientMonth && searchObject?.patientDay) {
        payload.age = `${searchObject.patientYear}-${searchObject.patientMonth}-${searchObject.patientDay}`;
      }

      // Filter only doctor's own patients
      //payload.myPatientsOnly = false;

      const doctorSearchPayload = {
        first_name: searchObject?.patientFirstName || '',
        last_name: searchObject?.patientLastName || '',
        cell_phone: searchObject?.patientNumber || '',
        id_number: searchObject?.idNumber || '',
        gender: payload.gender || '',
        birth_city_id: searchObject?.birthCity || '',
        patientYear: searchObject?.patientYear || '',
        patientMonth: searchObject?.patientMonth || '',
        patientDay: searchObject?.patientDay || '',
      };

      console.log("Searching patients with payload:", payload);
      console.log("Doctor search payload:", doctorSearchPayload);

      let response = await searchPatientByAllFieldApi(doctorSearchPayload);
      console.log("Full API response:", response);
      console.log("Response data:", response?.data);

      // Handle multiple possible response structures
      let patientList = [];
      if (response?.data?.success === true || response?.data?.res === true) {
        if (response?.data?.patients?.list) {
          patientList = response.data.patients.list;
        } else if (response?.data?.patients) {
          patientList = Array.isArray(response.data.patients) ? response.data.patients : [];
        } else if (response?.data?.list) {
          patientList = response.data.list;
        } else if (response?.data?.data) {
          patientList = Array.isArray(response.data.data) ? response.data.data : [];
        }
      } else if (Array.isArray(response?.data)) {
        patientList = response.data;
      }

      console.log("Parsed patient list:", patientList);
      setSearchPatientList(patientList);
      return patientList;
    } catch (error) {
      console.error("Patient search error:", error);
      setSearchPatientList([]);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  // MRZ Scanner - Load data from ID scanner
  const loadMRZData = async () => {
    try {
      const response = await fetch("http://localhost:5001/id-data?token=mySecret123");

      if (response.status === 204) {
        toast.warning("No data available yet.", { autoClose: 1500 });
        return;
      }

      if (!response.ok) {
        throw new Error("Unauthorized or server error");
      }

      const data = await response.json();

      if (data) {
        const newScan = {
          id: Date.now(),
          patientName: `${data.name} ${data.surname}`,
          idNumber: data.document_number,
          scannedAt: new Date().toISOString(),
          data: data
        };

        refetchScanHistory()

        setAddAppObject(prev => ({
          ...prev,
          patientFirstName: data.name,
          patientLastName: data.surname,
          patientGender: data.sex === "F" ? "female" : "male",
          idNumber: data.document_number,
          patientDay: data.birth_date.split("-")[2],
          patientMonth: data.birth_date.split("-")[1],
          patientYear: data.birth_date.split("-")[0]
        }));

        toast.success("ID Card scanned successfully!", { autoClose: 1500 });
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not connect to the ID scanner. Make sure it's running.", { autoClose: 1500 });
    }
  };
  // Select patient from scan history — same behavior as patient search Select button
  const loadHistoryScan = (scanData) => {
    const updatedAppObject = {
      ...addAppObject,
      patientId: scanData.profil_id || null,
      patientFirstName: scanData.patientFirstName || '',
      patientLastName: scanData.patientLastName || '',
      patientGender: scanData.patientGender?.toLowerCase() === 'f' || scanData.patientGender?.toLowerCase() === 'female' ? 'female' : 'male',
      idNumber: scanData.idNumber || '',
      appTime: addAppObject.appTime,
      popupType: addAppObject.popupType,
      appointmentId: addAppObject.appointmentId,
    };

    setAddAppObject(updatedAppObject);
    setShowScanHistory(false);

    if (onSuccess) {
      onSuccess(updatedAppObject, 'found');
    }
    handleClose();
  };

  // History pagination and filtering
  const getPaginatedHistory = () => {
    if( !scanHistory ) return
    const itemsPerPage = 10;
    return scanHistory.slice(0, historyPage * itemsPerPage);
  };

  const loadMoreHistory = () => {
    if( !scanHistory ) return 
    const itemsPerPage = 10;
    if (historyPage * itemsPerPage < scanHistory.length) {
      setHistoryPage(prev => prev + 1);
    }
  };

  const getFilteredHistory = () => {
    if( !scanHistory ) return
    if (!historySearch) return scanHistory;

    return scanHistory.filter(scan =>
      scan.patientName.toLowerCase().includes(historySearch.toLowerCase()) ||
      scan.idNumber.includes(historySearch)
    );
  };

  // Among the current search results, a national/global profile exists for the
  // same person (same name) as the locally-picked `patient`.
  const nationalProfileExistsFor = (patient) => {
    const fn = (patient?.first_name || "").trim().toLowerCase();
    const ln = (patient?.last_name || "").trim().toLowerCase();
    if (!fn || !ln) return false;
    return (searchPatientList || []).some(
      (p) =>
        !p?.is_local &&
        (p?.first_name || "").trim().toLowerCase() === fn &&
        (p?.last_name || "").trim().toLowerCase() === ln,
    );
  };

  // Handle patient selection from search results
  const handlePatientSelect = (type, patient) => {
    console.log("patient:", patient)

    // Picking a LOCAL profile while a GLOBAL one exists for the same person —
    // steer to the global profile instead of booking on the local duplicate.
    const isLocalPick = Boolean(patient?.is_local || patient?.profile_type === "local");
    if (isLocalPick && nationalProfileExistsFor(patient)) {
      toast.error("Use Global profile that is available.", { autoClose: 2500 });
      return;
    }

    // Parse date of birth
    const dobParts = patient.dob ? patient.dob.split('-') : ['', '', ''];

    // Build the updated object with patient data
    const updatedAppObject = {
      ...addAppObject,
      patientId: patient.id,
      patientFirstName: patient.first_name || '',
      patientLastName: patient.last_name || '',
      patientGender: patient.gender?.toLowerCase() === 'male' || patient.gender?.toLowerCase() === 'm' ? 'male' : 'female',
      patientNumber: patient.cell_phone || patient.phone || '',
      idNumber: patient.id_number || '',
      patientYear: dobParts[0] || '',
      patientMonth: dobParts[1] || '',
      patientDay: dobParts[2] || '',
      // Keep existing appointment time if it was set
      appTime: addAppObject.appTime,
      popupType: addAppObject.popupType,
      appointmentId: addAppObject.appointmentId
    };

    console.log("after update", updatedAppObject)

    // Update state
    setAddAppObject(updatedAppObject);

    // Call success callback with updated data
    if (onSuccess) {
      onSuccess(updatedAppObject, type);
    }
    handleClose();
  };


  const hasMatchingNationalProfile = (patients = searchPatientList) => {
    const firstName = (addAppObject?.patientFirstName || "").trim().toLowerCase();
    const lastName = (addAppObject?.patientLastName || "").trim().toLowerCase();

    const matchesName = (patient) => (
      (patient?.first_name || "").trim().toLowerCase() === firstName &&
      (patient?.last_name || "").trim().toLowerCase() === lastName
    );

    return nationalUsers.some((patient) => matchesName(patient)) || patients.some((patient) => (
      !patient?.is_local &&
      matchesName(patient)
    ));
  };

  const hasMatchingLocalProfile = (patients = searchPatientList) => {
    const firstName = (addAppObject?.patientFirstName || "").trim().toLowerCase();
    const lastName = (addAppObject?.patientLastName || "").trim().toLowerCase();

    return patients.some((patient) => (
      Boolean(patient?.is_local) &&
      (patient?.first_name || "").trim().toLowerCase() === firstName &&
      (patient?.last_name || "").trim().toLowerCase() === lastName &&
      (patient?.already_visited === true || patient?.already_visited === 1 ||
        patient?.is_my_patient === true ||
        Number(patient?.appointment_count_with_doctor ?? patient?.previous_appointments_count ?? 0) > 0)
    ));
  };

  const handleCreateProfile = async (profileType) => {
    let patients = searchPatientList;

    if (profileType === "local") {
      // Always re-run a name-only search so we reliably detect an existing
      // national/global (or local) profile for the same person.
      patients = await getPatientDetailsList({
        ...addAppObject,
        patientNumber: "",
        idNumber: "",
        patientGender: "",
        birthCity: "",
        municipality_id: "",
        patientYear: "",
        patientMonth: "",
        patientDay: "",
      });
    } else if (!hasMatchingNationalProfile(patients)) {
      patients = await getPatientDetailsList(addAppObject);
    }

    if (profileType === "national" && hasMatchingNationalProfile(patients)) {
      toast.error("Use Global profile that is available.", { autoClose: 2500 });
      return;
    }

    // Block creating a local profile when a national/global profile already
    // exists for the same person — steer the user to the global profile.
    if (profileType === "local" && hasMatchingNationalProfile(patients)) {
      toast.error("Global profile already exists, use it.", { autoClose: 2500 });
      return;
    }

    if (profileType === "local" && hasMatchingLocalProfile(patients)) {
      toast.error("Use your Local profile of this patient.", { autoClose: 2500 });
      return;
    }

    if (profileType === "national") {
      addAppObject?.popupType === "slot" ? handleValidateProfile() : handleAddAppointment();
    } else {
      handleAddAppointment("without-tracking");
    }
  };

  // Form actions
  const handleAddAppointment = (type) => {
    console.log(addAppObject, type)
    if (onSuccess) {
      onSuccess(addAppObject, type);
    }
    handleClose();
  };

  const handleUpdateSlotBlock = (type) => {
    if (validateAddAppointmentForm(type)) {
      if (onSuccess) {
        onSuccess(addAppObject, 'update-slot');
      }
      handleClose();
    }
  };

  const handleValidateProfile = () => {
    if (addAppObject?.popupType === "slot" && addAppObject?.patientId) {
      if (onSuccess) {
        onSuccess(addAppObject, 'validate-profile-slot');
      }
    } else {
      if (onSuccess) {
        onSuccess(addAppObject, 'validate-profile');
      }
    }
    handleClose();
  };

  const validateAddAppointmentForm = (type) => {
    let isValid = true;

    if (type === "slot") {
      if (!addAppObject?.patientFirstName?.trim()) {
        toast.error("First name is required", { autoClose: 1500 });
        isValid = false;
      }
      if (!addAppObject?.patientLastName?.trim()) {
        toast.error("Last name is required", { autoClose: 1500 });
        isValid = false;
      }
      if (!addAppObject?.patientGender) {
        toast.error("Gender is required", { autoClose: 1500 });
        isValid = false;
      }
    }

    return isValid;
  };

  const {
    data: citiesData,
    isLoading: isLoadingCitiesMunicipalities,
    refetch: refetchCitiesMunicipalities
  } = useCitiesMunicipalities();

  const handleClose = () => {
    setAddAppObject({});
    setAddAppObjectInitial({});
    setSearchPatientList([]);
    setIsUpdated(false);
    if (onClose) onClose();
  };

  // Fetch cities and municipalities on component mount
  useEffect(() => {
    const fetchCitiesData = async () => {
      try {
        setLoadingCities(true);

        // Extract unique wilayas from the fetched data
        if (citiesData && Array.isArray(citiesData)) {
          const wilayas = citiesData.map(city => ({
            id: city.id,
            code: city.code || city.id,
            name: city.name,
            name_ar: city.name // API doesn't provide Arabic names, using same name
          }));

          setWilayasList(wilayas);
        }
      } catch (error) {
        console.info("Error fetching cities:", error);
        toast.error("Error loading cities data for reason " + error, { autoClose: 1500 });
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCitiesData();
  }, [citiesData]);


  // Update communes list when birthCity changes
  useEffect(() => {
    if (addAppObject?.birthCity && citiesData.length > 0) {
      const selectedCity = citiesData.find(city => city.id === Number(addAppObject.birthCity));

      if (selectedCity && selectedCity.municipalities) {
        const communes = selectedCity.municipalities.map(municipality => ({
          id: municipality.id,
          name: municipality.name,
          name_ar: municipality.name, // API doesn't provide Arabic names
          city_id: municipality.city_id
        }));

        setCommunesList(communes);
      } else {
        setCommunesList([]);
      }
    } else {
      setCommunesList([]);
    }
  }, [addAppObject?.birthCity, citiesData]);

  if (!isOpen) return null;

  const showRightPanel = isSearching || searchPatientList?.length > 0 || (searchPatientList?.length === 0 && (addAppObject?.patientFirstName || addAppObject?.patientLastName));

  return (
    <div className="popup holidayPopup AddAppointmentModal AddAppointmentModalMobile">
      <div className={`popup-content ${showRightPanel ? "increaseWidth" : ""}`}>
        <div className="addNursePopup addAppointment">

          {/* Header Section */}
          <header className="modal-header">
            <h2 className="modal-title">Add Appointment</h2>
            <div className="header-actions">
              <button className="mrzScannerBtn" onClick={loadMRZData} type="button" style={{ display: "none" }}>
                <span className="icon">🔍</span>
                <span>Scan ID / Scanner ID</span>
              </button>


              <div className="scan-history-wrapper" ref={scanHistoryRef}>
                <button
                  className="mrzHistoryBtn"
                  onClick={() => {
                    const newShowState = !showScanHistory;
                    setShowScanHistory(newShowState);
                    // Mark as seen when opening dropdown
                    if (newShowState && scanHistory && scanHistory.some(scan => !scan.is_seen)) {
                      markCurrentScansAsSeen();
                    }
                  }}
                >
                  <span className="icon">📋</span> Scanned ID History
                  {scanHistory && scanHistory.filter(scan => !scan.is_seen).length > 0 && (
                    <span className="unseen-badge">({scanHistory.filter(scan => !scan.is_seen).length})</span>
                  )}
                </button>

                {showScanHistory && (
                  <div className="scan-history-dropdown">
                    <div className="history-header">
                      <span className="history-title">Scan History</span>
                      <button
                        className="mrzRefreshBtn"
                        onClick={handleScansReload}
                        type="button"
                        title="Refresh scan history"
                      >
                        🔄
                      </button>
                    </div>
                    <div className="history-list">
                      {scanHistory && scanHistory.length === 0 ? (
                        <div className="no-history">No scans yet</div>
                      ) : (
                        scanHistory?.map((scan) => (
                          <div
                            key={scan.id}
                            className="history-item"
                          >
                            <div className="history-item-content">
                              <div className="patient-info">
                                <span className="patient-name">
                                  {scan.patientFirstName} {scan.patientLastName}
                                </span>
                                <span className="patient-id">ID: {scan.idNumber}</span>
                                <span className="scan-date">
                                  {moment(scan.scannedAt).format("DD/MM/YYYY HH:mm")}
                                </span>
                              </div>
                              <button
                                className="history-select-btn"
                                type="button"
                                onClick={() => loadHistoryScan(scan)}
                              >
                                Select
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>


              <button onClick={handleClose} className="cancelBtn" type="button">
                <img src={cancelIcon} alt="Close" />
              </button>
            </div>
          </header>

          {/* Main Content */}
          <div className="patientAddBox">
            {/* Form Section */}
            <div className="formWrapper">
              <ul className="addPatientList">
                {/* Name Fields */}
                <li className="form-row form-row-2col">
                  <div className="form-field">
                    <label>First Name</label>
                    <input
                      type="text"
                      placeholder="First name (in French letters)"
                      value={addAppObject?.patientFirstName || ''}
                      onChange={e => handleInputChange("patientFirstName", e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label>Last Name</label>
                    <input
                      type="text"
                      placeholder="Last name (in French letters)"
                      value={addAppObject?.patientLastName || ''}
                      onChange={e => handleInputChange("patientLastName", e.target.value)}
                    />
                  </div>
                </li>

                {/* Gender Field */}
                <li className="genderField">
                  <label>Gender</label>
                  <div className="genderRadioGroup">
                    <div className="radioOption">
                      <input
                        type="radio"
                        id="gender-male"
                        name="gender"
                        value="male"
                        checked={addAppObject?.patientGender === "male"}
                        onChange={e => handleInputChange("patientGender", e.target.value)}
                      />
                      <label htmlFor="gender-male" className="radioLabel">Male</label>
                    </div>
                    <div className="radioOption">
                      <input
                        type="radio"
                        id="gender-female"
                        name="gender"
                        value="female"
                        checked={addAppObject?.patientGender === "female"}
                        onChange={e => handleInputChange("patientGender", e.target.value)}
                      />
                      <label htmlFor="gender-female" className="radioLabel">Female</label>
                    </div>
                  </div>
                </li>

                {/* Location Fields */}
                <li className="form-row form-row-2col">
                  <div className="form-field">
                    <label>Birth City/Wilaya</label>
                    <select
                      value={addAppObject?.birthCity || ''}
                      onChange={e => handleInputChange("birthCity", e.target.value)}
                      disabled={loadingCities}
                    >
                      <option value="">
                        {loadingCities ? "Loading cities..." : "Select birth city/wilaya"}
                      </option>
                      {wilayasList?.map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Birth Commune</label>
                    <select
                      value={addAppObject?.municipality_id || ''}
                      onChange={e => handleInputChange("municipality_id", e.target.value)}
                      disabled={!addAppObject?.birthCity || communesList.length === 0 || loadingCities}
                    >
                      <option value="">
                        {!addAppObject?.birthCity
                          ? "Select city first"
                          : communesList.length === 0
                            ? "No communes available"
                            : "Select birth commune"}
                      </option>
                      {communesList?.map((commune) => (
                        <option key={commune.id} value={commune.id}>{commune.name}</option>
                      ))}
                    </select>

                  </div>
                </li>

                {/* Date of Birth */}
                <li className="dateOfBirthCont">
                  <label>Date of Birth</label>
                  <ul className="dob-selects">
                    <li>
                      <select
                        value={addAppObject?.patientMonth || ''}
                        onChange={e => handleInputChange("patientMonth", e.target.value)}
                      >
                        <option value="">Month</option>
                        <option value="00">Unknown / Inconnu</option>
                        {months?.map((item, index) => (
                          <option key={index} value={index + 1 < 10 ? "0" + (index + 1) : index + 1}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </li>
                    <li>
                      <select
                        value={addAppObject?.patientDay || ''}
                        onChange={e => handleInputChange("patientDay", e.target.value)}
                      >
                        <option value="">Day</option>
                        <option value="00">Unknown / Inconnu</option>
                        {daysarray?.map((item, index) => (
                          <option key={index} value={item}>{item}</option>
                        ))}
                      </select>
                    </li>
                    <li>
                      <select
                        value={addAppObject?.patientYear || ''}
                        onChange={e => handleInputChange("patientYear", e.target.value)}
                      >
                        <option value="">Year</option>
                        {years?.map((year, index) => (
                          <option key={index} value={year}>{year}</option>
                        ))}
                      </select>
                    </li>
                  </ul>
                </li>

                {/* Section Divider */}
                <li className="sectionDivider">
                  <span>ID & Contact Information</span>
                </li>

                {/* Phone Number */}
                <li className="form-field-full">
                  <label>Phone Number</label>
                  <input
                    type="text"
                    placeholder="Phone Number (0XXXXXXXXX or +213XXXXXXXXX)"
                    value={addAppObject?.patientNumber || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/[^\d+]/g, '');
                      handleInputChange("patientNumber", value);
                    }}
                    maxLength={15}
                    className={addAppObject?.patientNumber && !validateDzPhoneNumber(addAppObject?.patientNumber).isValid ? 'input-error' : ''}
                  />
                  {addAppObject?.patientNumber && !validateDzPhoneNumber(addAppObject?.patientNumber).isValid && (
                    <span className="error-message">
                      {validateDzPhoneNumber(addAppObject?.patientNumber).message}
                    </span>
                  )}
                </li>

                {/*
                {!((userData?.users?.type === "Doctor") || (userData?.users?.type === "Nurse")) && (
                  <li className="form-field-full">
                    <label>Id Number</label>
                    <input
                      type="text"
                      placeholder="18-digit Algerian ID Number"
                      value={addAppObject?.idNumber || ''}
                      onChange={e => handleInputChange("idNumber", e.target.value)}
                      maxLength={18}
                      className={addAppObject?.idNumber && !validateDzIdNumber(addAppObject?.idNumber).isValid ? 'input-error' : ''}
                    />
                    {addAppObject?.idNumber && !validateDzIdNumber(addAppObject?.idNumber).isValid && (
                      <span className="error-message">
                        {validateDzIdNumber(addAppObject?.idNumber).message}
                      </span>
                    )}
                  </li>
                )}
                */}

                {/* Action Buttons */}
                <li className="form-actions">
                  <div className="btnGroup">
                    <button
                      type="button"
                      className="startBtn"
                      disabled={
                        !addAppObject?.patientFirstName?.trim() ||
                        !addAppObject?.patientLastName?.trim() ||
                        !addAppObject?.patientGender ||
                        (addAppObject?.appointmentId && !isUpdated)
                      }
                      onClick={() => addAppObject?.appointmentId ? handleUpdateSlotBlock("slot") : handleAddAppointment("slot")}
                    >
                      {addAppObject?.appointmentId ? "Update Slot Block" : "Slot Block"}
                    </button>

                    {(userData?.users?.type === "Doctor" || userData?.users?.type === "Nurse") && (
                      <button
                        type="button"
                        className="startBtn"
                        disabled={
                          !addAppObject?.patientFirstName?.trim() ||
                          !addAppObject?.patientLastName?.trim() ||
                          !addAppObject?.patientGender
                        }
                        title={
                          hasNationalDuplicate
                            ? "Un profil national identique existe déjà"
                            : "Créer un profil local"
                        }
                        onClick={() => handleCreateProfile("local")}
                      >
                        Créer un Profil Locale
                      </button>


                    )}
                  </div>

                  {/*
                  {!((userData?.users?.type === "Doctor") || (userData?.users?.type === "Nurse")) && (
                    <button
                      type="button"
                      className="startBtn startBtn-primary"
                      disabled={
                        !addAppObject?.patientFirstName ||
                        !addAppObject?.patientLastName ||
                        !addAppObject?.patientGender ||
                        !addAppObject?.birthCity ||
                        !addAppObject?.municipality_id ||
                        !addAppObject?.patientMonth ||
                        !addAppObject?.patientDay ||
                        !addAppObject?.patientYear ||
                        !addAppObject?.patientNumber ||
                        !addAppObject?.idNumber ||
                        !validateDzIdNumber(addAppObject?.idNumber).isValid ||
                        !validateDzPhoneNumber(addAppObject?.patientNumber).isValid
                      }
                      onClick={() => handleCreateProfile("national")}
                    >
                      Créer un Profil {addAppObject?.popupType === "slot" ? "Nationale" : "National"}
                    </button>
                  )}
                  */}
                </li>
              </ul>
            </div>

            {/* Patient Search Results */}
            {isSearching && (
              <div className="foundProfileWrapper">
                <h3>Searching...</h3>
                <div className="scrollSection" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                  <span>Loading patients...</span>
                </div>
              </div>
            )}

            {!isSearching && searchPatientList && searchPatientList.length > 0 ? (
              <PatientSearchResult
                searchPatientList={searchPatientList}
                addAppObject={addAppObject}
                setAddAppObject={setAddAppObject}
                handleAddAppointment={handleAddAppointment}
                handlePatientSelect={handlePatientSelect}
              />
            ) : !isSearching && searchPatientList && searchPatientList.length === 0 && (addAppObject?.patientFirstName || addAppObject?.patientLastName) ? (
              <div className="foundProfileWrapper">
                <h3>No Results</h3>
                <div className="scrollSection" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', color: '#6b7280' }}>
                  <span>No matching patients found</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAppointmentForm;
